<template>
    <IntegrationModal
        :title="$t('vue.teams')"
        documentation-url=""
        :invalid-validation="v$.$invalid"
        :loading="loading"
        :edit-mode="integration !== undefined"
        @close="$emit('close')"
        @create="createIntegration"
        @edit="updateIntegration"
    >
        <template #inputs>
            <div class="mb-6 w-full">
                <TextInput
                    v-model="v$.name.$model"
                    class="mt-6"
                    :placeholder="
                        $t('vue.enter_your_preferred_integration_name')
                    "
                    :label="$t('vue.name')"
                    :error="getError('name')"
                />

                <TextInput
                    v-model="v$.url.$model"
                    class="mt-6 w-full"
                    :placeholder="$t('vue.your_teams_webhook_url')"
                    :label="$t('vue.webhook_url')"
                    required
                    :error="getError('url')"
                />
            </div>
        </template>
    </IntegrationModal>
</template>
<script lang="ts" setup>
import route from "ziggy-js";
import { create } from "@composables/useNotifications";
import useVuelidate from "@vuelidate/core";
import { maxLength, required } from "@vuelidate/validators";
import type IInputErrors from "@/types/Atomic/IInputErrors";
import type { PropType } from "vue";
import type { IIntegration, ITeams } from "@/types/Integrations/IIntegration";
import useIntegrations from "../../composables/useIntegrations";
import { url } from "@vuelidate/validators";

const props = defineProps({
    integration: {
        type: Object as PropType<IIntegration<ITeams>>,
        required: false,
        default: undefined,
    },
});
const emit = defineEmits(["close"]);

const loading = ref(false);
const errors = ref<IInputErrors>({});

const { teamsIntegrationModalOpened } = useIntegrations();
const state = reactive({
    name: props.integration?.name || "",
    url: props.integration?.data.url || "",
});

const rules = {
    name: {
        maxLength: maxLength(255),
    },
    url: {
        required,
        maxLength: maxLength(5000),
        url,
    },
};

const v$ = useVuelidate(rules, state);

const getError = (field: string) => {
    return v$.value[field]?.$errors[0]?.$message || errors.value?.[field] || "";
};

const createIntegration = () => {
    v$.value.$touch();
    if (v$.value.$error) {
        () => undefined;
    } else {
        loading.value = true;
        router.post(
            route("integrations.teams.store"),
            {
                name: state.name,
                url: state.url,
            },
            {
                preserveScroll: true,
                onSuccess: () => {
                    create({
                        title: $t("vue.integration_added"),
                        text: $t("vue.teams_integration_added"),
                        type: "success",
                    });
                    teamsIntegrationModalOpened.value = false;
                },
                onError: (err) => {
                    errors.value = err;
                    create({
                        title: $t("vue.integration_not_added"),
                        text:
                            err.message ??
                            $t("vue.error_adding_teams_integration"),
                        type: "error",
                    });
                },
                onFinish: () => {
                    loading.value = false;
                },
            },
        );
    }
};

const updateIntegration = () => {
    v$.value.$touch();
    if (v$.value.$error || !props.integration) {
        () => undefined;
    } else {
        loading.value = true;
        router.put(
            route("integrations.teams.update", {
                integration: props.integration.id,
            }),
            {
                name: state.name,
                url: state.url,
            },
            {
                preserveScroll: true,
                onSuccess: () => {
                    emit("close");
                    create({
                        title: $t("vue.integration_updated"),
                        type: "success",
                    });
                },
                onError: (err) => {
                    errors.value = err;
                    create({
                        title: $t("vue.integration_not_updated"),
                        text:
                            err.message ??
                            $t("vue.error_updating_teams_integration"),
                        type: "error",
                    });
                },
                onFinish: () => {
                    loading.value = false;
                },
            },
        );
    }
};
</script>

<style scoped></style>
